import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import VendorServiceSkeleton from '../shared/VendorServiceSkeleton';
import MarketingListingCard from './MarketingListingCard';
import MarketingReveal from './MarketingReveal';

const VendorServices = ({
  title = 'Vendor Services',
  data = [],
  isLoading = false,
  error = null,
  emptyMessage = 'No services available',
  emptyDescription = 'Check back later for services',
  linkPrefix = '/services',
  priceLabel = 'starting from',
  showArrowInTitle = true,
  className = '',
  categorySlug = null,
  viewAllLink = null,
  hideWhenEmpty = true,
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const vendorServices = data;

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -400 : 400,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    handleScroll();
    if (scrollRef.current) {
      const resizeObserver = new ResizeObserver(() => handleScroll());
      resizeObserver.observe(scrollRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [vendorServices.length]);

  if (!isLoading && !error && vendorServices.length === 0 && hideWhenEmpty) {
    return null;
  }

  const sectionLink =
    viewAllLink ||
    (categorySlug
      ? `/category/${categorySlug}?title=${encodeURIComponent(title)}`
      : null);

  const railFadeClass =
    className?.includes('bg-m-canvas-light')
      ? 'marketing-rail-cream'
      : 'marketing-rail-surface';

  return (
    <section
      className={`relative overflow-hidden border-b border-m-border-light py-20 sm:py-24 ${
        className || 'bg-m-surface'
      }`}
    >
      <MarketingReveal className="page-container relative z-10">
        <div className="mb-10 flex items-center justify-between gap-4">
          {sectionLink ? (
            <Link
              to={sectionLink}
              className="marketing-display-md group flex items-center text-m-ink transition-opacity hover:opacity-70"
            >
              {title}
              {showArrowInTitle ? (
                <ArrowRight className="ml-2 h-5 w-5 text-m-ink-muted transition-transform duration-300 group-hover:translate-x-0.5" />
              ) : null}
            </Link>
          ) : (
            <h2 className="marketing-display-md flex items-center text-m-ink">
              {title}
              {showArrowInTitle ? (
                <ArrowRight className="ml-2 h-5 w-5 text-m-ink-muted" />
              ) : null}
            </h2>
          )}

          {!isLoading && !error && vendorServices.length > 0 ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scroll('left')}
                disabled={!showLeftArrow}
                className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                  showLeftArrow
                    ? 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
                    : 'cursor-not-allowed border-transparent bg-m-canvas-light text-m-ink-faint'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scroll('right')}
                disabled={!showRightArrow}
                className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                  showRightArrow
                    ? 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
                    : 'cursor-not-allowed border-transparent bg-m-canvas-light text-m-ink-faint'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>

        {isLoading ? (
          <VendorServiceSkeleton count={6} />
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-red-600">Failed to load services</p>
            <p className="mt-1 text-sm text-m-ink-muted">Please try again later</p>
          </div>
        ) : vendorServices.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-m-ink">{emptyMessage}</p>
            <p className="mt-1 text-sm text-m-ink-muted">{emptyDescription}</p>
          </div>
        ) : (
          <div className={`marketing-rail ${railFadeClass}`}>
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex gap-5 overflow-x-auto scroll-smooth pb-2 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {vendorServices.map((service) => (
                <MarketingListingCard
                  key={service.id}
                  compact
                  className="w-[min(220px,70vw)] shrink-0 sm:w-[200px]"
                  href={`${linkPrefix}/${service.id}`}
                  image={service.image}
                  title={service.title}
                  location={service.location}
                  rating={service.rating}
                  price={service.price}
                  priceSuffix={priceLabel}
                  reviewCount={service.reviewCount}
                  featured={service.isFeatured}
                />
              ))}
            </div>
          </div>
        )}
      </MarketingReveal>
    </section>
  );
};

export default VendorServices;
